import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import type { ResumeDocument } from "@/types/cv";

// ATS-friendly single-column resume in real, selectable text using the built-in
// Helvetica family (no remote font fetch). Layout mirrors a clean recruiter-ready
// CV: header, summary, experience, education, skills.

const ID_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Agu",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const formatMonth = (value: string | null): string => {
  if (!value) return "";
  const [year, month] = value.split("-");
  const index = Number(month) - 1;
  if (Number.isNaN(index) || index < 0 || index > 11) return year;
  return `${ID_MONTHS[index]} ${year}`;
};

const dateRange = (
  start: string | null,
  end: string | null,
  isCurrent: boolean,
): string => {
  const from = formatMonth(start);
  const to = isCurrent ? "Sekarang" : formatMonth(end);
  if (from && to) return `${from} - ${to}`;
  return from || to;
};

const styles = StyleSheet.create({
  page: {
    paddingVertical: 22,
    paddingHorizontal: 28,
    fontFamily: "Helvetica",
    fontSize: 10,
    lineHeight: 1.4,
    color: "#1a1a1a",
  },
  name: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 9,
  },
  contact: {
    fontSize: 9,
    textAlign: "center",
    color: "#444",
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#999",
    paddingBottom: 2,
    marginTop: 14,
    marginBottom: 6,
  },
  summary: { marginBottom: 2 },
  entry: { marginBottom: 8 },
  entryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  entryTitle: { fontFamily: "Helvetica-Bold", fontSize: 10.5 },
  entryMeta: { color: "#555", fontSize: 9.5 },
  entryDate: { color: "#555", fontSize: 9.5, textAlign: "right" },
  bulletRow: { flexDirection: "row", marginTop: 2 },
  bulletDot: { width: 10, fontSize: 10 },
  bulletText: { flex: 1 },
  skillLine: { marginBottom: 3 },
  skillLabel: { fontFamily: "Helvetica-Bold" },
});

const joinContact = (parts: Array<string | undefined | null>): string =>
  parts.map((p) => p?.trim()).filter(Boolean).join("  |  ");

interface ResumePdfProps {
  resume: ResumeDocument;
}

export function ResumePdfDocument({ resume }: ResumePdfProps) {
  const id = resume.personal_identity;
  const location = [id.location.city, id.location.country]
    .filter(Boolean)
    .join(", ");
  const contact = joinContact([
    location,
    id.email,
    id.phone_number,
    id.linkedin,
  ]);
  const info = resume.additional_information;

  return (
    <Document
      title={`CV ${id.name || "Hyred"}`}
      author={id.name || undefined}
    >
      <Page size="A4" style={styles.page}>
        <Text style={styles.name}>{id.name || "Nama Lengkap"}</Text>
        {contact ? <Text style={styles.contact}>{contact}</Text> : null}

        {resume.professional_summary.trim() ? (
          <View>
            <Text style={styles.sectionTitle}>Ringkasan</Text>
            <Text style={styles.summary}>{resume.professional_summary}</Text>
          </View>
        ) : null}

        {resume.working_experience.length > 0 ? (
          <View>
            <Text style={styles.sectionTitle}>Pengalaman</Text>
            {resume.working_experience.map((exp, i) => (
              <View key={i} style={styles.entry} wrap={false}>
                <View style={styles.entryHeader}>
                  <Text style={styles.entryTitle}>
                    {exp.title}
                    {exp.company ? ` - ${exp.company}` : ""}
                  </Text>
                  <Text style={styles.entryDate}>
                    {dateRange(exp.start_date, exp.end_date, exp.is_current)}
                  </Text>
                </View>
                {exp.location ? (
                  <Text style={styles.entryMeta}>{exp.location}</Text>
                ) : null}
                {exp.points
                  .filter((p) => p.trim())
                  .map((point, j) => (
                    <View key={j} style={styles.bulletRow}>
                      <Text style={styles.bulletDot}>•</Text>
                      <Text style={styles.bulletText}>{point}</Text>
                    </View>
                  ))}
              </View>
            ))}
          </View>
        ) : null}

        {resume.education.length > 0 ? (
          <View>
            <Text style={styles.sectionTitle}>Pendidikan</Text>
            {resume.education.map((edu, i) => (
              <View key={i} style={styles.entry} wrap={false}>
                <View style={styles.entryHeader}>
                  <Text style={styles.entryTitle}>{edu.institution}</Text>
                  <Text style={styles.entryDate}>
                    {dateRange(edu.start_date, edu.end_date, false)}
                  </Text>
                </View>
                <Text style={styles.entryMeta}>
                  {[edu.degree, edu.major].filter(Boolean).join(", ")}
                  {edu.gpa ? `  •  IPK ${edu.gpa}` : ""}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {info.technical_skills.length > 0 ||
        info.soft_skills.length > 0 ||
        info.languages.length > 0 ? (
          <View>
            <Text style={styles.sectionTitle}>Skill</Text>
            {info.technical_skills.length > 0 ? (
              <Text style={styles.skillLine}>
                <Text style={styles.skillLabel}>Teknis: </Text>
                {info.technical_skills.join(", ")}
              </Text>
            ) : null}
            {info.soft_skills.length > 0 ? (
              <Text style={styles.skillLine}>
                <Text style={styles.skillLabel}>Soft skill: </Text>
                {info.soft_skills.join(", ")}
              </Text>
            ) : null}
            {info.languages.length > 0 ? (
              <Text style={styles.skillLine}>
                <Text style={styles.skillLabel}>Bahasa: </Text>
                {info.languages
                  .filter((l) => l.name.trim())
                  .map((l) =>
                    l.proficiency ? `${l.name} (${l.proficiency})` : l.name,
                  )
                  .join(", ")}
              </Text>
            ) : null}
          </View>
        ) : null}
      </Page>
    </Document>
  );
}
